import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { dashboardService } from '../services/dashboardService';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import StatCard from '../components/dashboard/StatCard';
import ChartCard from '../components/dashboard/ChartCard';
import TopTasksTable from '../components/dashboard/TopTasksTable';
import TableSkeleton from '../components/common/TableSkeleton';
import Loading from '../components/common/Loading';

const COLORS = ['#2563eb', '#0ea5e9', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

const getDefaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10)
  };
};

const MyDashboard = () => {
  const [dateRange, setDateRange] = useState(getDefaultRange);
  const [status, setStatus] = useState('');
  const [granularity, setGranularity] = useState('day');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCustomer, setActiveCustomer] = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await dashboardService.getMyDashboard({
        start: dateRange.start,
        end: dateRange.end,
        status: status || undefined,
        granularity
      });
      setDashboard(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end, status, granularity]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const filteredTopTasks = useMemo(() => {
    const tasks = dashboard?.tables?.topActiveTasks || [];
    if (!activeCustomer) return tasks;
    return tasks.filter((task) => (task.customerId || task.project || 'Internal / Unassigned') === activeCustomer);
  }, [dashboard, activeCustomer]);

  const summary = dashboard?.summary || {};

  const filters = (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="my-dashboard-start">Start Date</label>
        <input
          id="my-dashboard-start"
          aria-label="My dashboard start date"
          type="date"
          value={dateRange.start}
          onChange={(event) => setDateRange((prev) => ({ ...prev, start: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="my-dashboard-end">End Date</label>
        <input
          id="my-dashboard-end"
          aria-label="My dashboard end date"
          type="date"
          value={dateRange.end}
          onChange={(event) => setDateRange((prev) => ({ ...prev, end: event.target.value }))}
          className="w-full border rounded px-3 py-2"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="my-dashboard-status">Status</label>
        <select
          id="my-dashboard-status"
          aria-label="Filter status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">All Statuses</option>
          <option value="Planning">Planning</option>
          <option value="In Progress">In Progress</option>
          <option value="In Review">In Review</option>
          <option value="Blocked">Blocked</option>
          <option value="On Hold">On Hold</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="my-dashboard-granularity">Trend View</label>
        <select
          id="my-dashboard-granularity"
          aria-label="Trend granularity"
          value={granularity}
          onChange={(event) => setGranularity(event.target.value)}
          className="w-full border rounded px-3 py-2"
        >
          <option value="day">Daily</option>
          <option value="week">Weekly</option>
        </select>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title="My Dashboard" description="Personal execution and progress insights." filters={filters}>
        <Loading />
        <TableSkeleton rows={6} columns={5} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Dashboard" description="Personal execution and progress insights." filters={filters}>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">{error}</div>}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Tasks Completed" value={summary.tasksCompleted || 0} />
        <StatCard title="Hours Logged" value={summary.hoursLogged || 0} />
        <StatCard title="Tasks In Progress" value={summary.tasksInProgress || 0} />
        <StatCard title="On-time Rate" value={summary.onTimeRate !== null && summary.onTimeRate !== undefined ? `${summary.onTimeRate}%` : 'N/A'} />
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard title="Consistency Streak" value={`${summary.consistencyStreak || 0} days`} subtitle="Longest daily streak in range" />
        <StatCard title="Blockers Frequency" value={summary.blockersFrequency || 0} subtitle="Updates that mention blocker themes" />
        <StatCard title="Focus Score" value={`${summary.focusScore || 0}%`} subtitle="Hours concentrated in top 3 projects/customers" />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Hours Trend" subtitle={`By ${granularity}`}>
          {dashboard?.series?.hoursTrend?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dashboard.series.hoursTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="hours" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-500">No data in this range.</div>
          )}
        </ChartCard>

        <ChartCard title="Completed Projects per Week">
          {dashboard?.series?.completedPerWeek?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dashboard.series.completedPerWeek}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-500">No completions in this range.</div>
          )}
        </ChartCard>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ChartCard
          title="Time Distribution"
          subtitle="Click a segment to filter top tasks"
          action={
            activeCustomer ? (
              <button
                type="button"
                onClick={() => setActiveCustomer('')}
                className="text-xs text-primary hover:underline"
              >
                Clear Filter
              </button>
            ) : null
          }
        >
          {dashboard?.series?.hoursByCustomer?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={dashboard.series.hoursByCustomer}
                  dataKey="hours"
                  nameKey="label"
                  innerRadius={60}
                  outerRadius={100}
                  onClick={(entry) => setActiveCustomer(entry.label)}
                >
                  {dashboard.series.hoursByCustomer.map((entry, index) => (
                    <Cell
                      key={entry.key}
                      fill={COLORS[index % COLORS.length]}
                      stroke={activeCustomer === entry.label ? '#111827' : 'none'}
                      strokeWidth={activeCustomer === entry.label ? 2 : 0}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${Number(value).toFixed(2)}h`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-500">No distribution data in this range.</div>
          )}
        </ChartCard>

        <div className="xl:col-span-2">
          <ChartCard
            title="My Top Active Tasks"
            subtitle={activeCustomer ? `Filtered by: ${activeCustomer}` : 'Sorted by due date'}
          >
            <TopTasksTable tasks={filteredTopTasks} />
          </ChartCard>
        </div>
      </section>
    </DashboardLayout>
  );
};

export default MyDashboard;
